import React, { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, X } from "lucide-react";
import { naira, formatApiError } from "../../lib/format";
import api from "../../lib/api";
import MoneyField from "./MoneyField";

/**
 * Renewal modal — renews a lease for another term (default 12 months).
 * Per Modav policy, only rent is charged. Caution/agreement/service/commission
 * stay at zero. End-date auto-syncs with start + term.
 */
export default function LeaseRenewalModal({ lease, onClose, onCreated }) {
  // Initial draft: start = day after current end; term = 12 months; rent = current
  const [draft, setDraft] = useState(() => {
    let startISO = "";
    let endISO = "";
    if (lease.lease_end_date) {
      try {
        const d = new Date(lease.lease_end_date);
        d.setDate(d.getDate() + 1);
        startISO = d.toISOString().slice(0, 10);
        const e = new Date(d);
        e.setFullYear(e.getFullYear() + 1);
        endISO = e.toISOString().slice(0, 10);
      } catch { /* no-op */ }
    }
    return {
      lease_start_date: startISO,
      lease_end_date: endISO,
      term_months: 12,
      rent_amount: lease.rent_amount || 0,
    };
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => {
    setDraft((prev) => {
      const next = { ...prev, [k]: v };
      // Sync end-date when start or term changes
      if (k === "lease_start_date" || k === "term_months") {
        try {
          const d = new Date(next.lease_start_date);
          d.setMonth(d.getMonth() + Number(next.term_months || 12));
          next.lease_end_date = d.toISOString().slice(0, 10);
        } catch { /* no-op */ }
      }
      return next;
    });
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/leases/${lease.id}/renew`, {
        lease_start_date: draft.lease_start_date,
        term_months: Number(draft.term_months || 12),
        rent_amount: draft.rent_amount ? Number(draft.rent_amount) : undefined,
      });
      toast.success("Renewal lease created — rent only, ready to send for signing");
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-modav p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="renewal-modal">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-serif text-xl font-bold">Renew Lease</h3>
            <p className="text-xs text-[#5E7166] mt-0.5">Renewal carries <b>Rent only</b> — caution, agreement, service & commission are zero by policy.</p>
          </div>
          <button onClick={onClose} data-testid="renewal-close-btn"><X size={20}/></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Start Date</span>
              <input
                type="date"
                value={draft.lease_start_date}
                onChange={(e) => update("lease_start_date", e.target.value)}
                className="input-modav mt-1"
                data-testid="renew-start-date"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Term (months)</span>
              <input
                type="number" min={1} max={120}
                value={draft.term_months}
                onChange={(e) => update("term_months", e.target.value)}
                className="input-modav mt-1"
                data-testid="renew-term"
              />
            </label>
          </div>

          <div className="text-xs text-[#5E7166]">End date: <b className="text-[#1A2E24]">{draft.lease_end_date || "—"}</b></div>

          <MoneyField
            label="Rent for renewal"
            value={draft.rent_amount}
            onChange={(v) => update("rent_amount", v)}
            testid="renew-rent"
            hint="Pre-filled from current lease. Use the Pricing module's latest rate if you want a different rent."
          />

          <div className="bg-[#0E5C3A]/5 border border-[#0E5C3A]/20 rounded-lg p-3 flex items-center justify-between" data-testid="renewal-total">
            <span className="text-sm text-[#5E7166] font-semibold">Total Payable (rent only)</span>
            <span className="naira-total text-2xl">{naira(Number(draft.rent_amount) || 0)}</span>
          </div>

          <div className="text-[11px] text-[#5E7166] bg-[#FDF6E3]/40 p-2 rounded">
            The current lease will be marked <b>renewed</b>. A fresh draft lease is created on the same unit for the new period and will be ready to send to the tenant for digital signature.
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="renew-cancel-btn">Cancel</button>
            <button onClick={confirm} disabled={saving} className="btn-gold flex-1 justify-center" data-testid="renew-confirm-btn">
              <RefreshCw size={14}/> {saving ? "Generating…" : "Generate Renewal"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
